// A project's enquiries: what buyers sent from the tour (newest first, new ones marked), and the form
// itself: the Enquire tab, the loading-screen form and who it emails. Leads are written by the server
// only; studios mark them read through mark_leads_read().
import {
  getProject, getPanoramas, getLeads, markLeadsRead, openBilling, update, getDraftManifest, manifestBrand, uploadFile, syncPublished,
  unreadLeads, type Studio, type LeadRow, type ProjectBrand,
} from '../data';
import { html, icon, shell, toast, setShellProject, setUnread, $, $$, type Raw } from '../ui';

const fmtWhen = (iso: string) => {
  const d = new Date(iso), now = new Date();
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  const time = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  return diff === 0 ? `Today, ${time}` : diff === 1 ? `Yesterday, ${time}` : d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
};
const fromWhere = (l: LeadRow) => `${l.source === 'tab' ? 'Enquire tab' : 'Loading screen'}${l.room ? ` · in ${l.room}` : ''}`;

/** Every enquiry as a CSV the studio can open in a spreadsheet. */
export function downloadLeads(name: string, leads: LeadRow[]): void {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [['Date', 'Name', 'Email', 'Phone', 'Message', 'Room', 'From', 'Happy to be contacted'], ...leads.map((l) => [new Date(l.created_at).toLocaleString('en-AU'), l.name, l.email, l.phone, l.message ?? '', l.room ?? '', l.source === 'tab' ? 'Enquire tab' : 'Loading screen', l.consent ? 'Yes' : 'No'])];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' }));
  a.download = `${name.replace(/[^\w -]/g, '').trim() || 'tour'} enquiries.csv`; a.click();
}

export async function enquiriesView(root: HTMLElement, studio: Studio, projectId: string): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'enquiries', project: { id: projectId } });
  page.innerHTML = '<div class="loading-page">Loading enquiries</div>';
  const project = await getProject(projectId);
  if (!project) { page.innerHTML = '<div class="empty"><h2>Project not found</h2></div>'; return; }
  setShellProject(root, project, () => openBilling(project.id), unreadLeads(project.id));
  const [panos, loaded] = await Promise.all([getPanoramas(projectId), getLeads(projectId).catch(() => [] as LeadRow[])]);
  let leads = loaded;
  // read marks need the 20261023 migration; before it, nothing is marked new
  const marks = leads.some((l) => 'read_at' in l);
  let filter: 'all' | 'new' = 'all';

  const brand: ProjectBrand = structuredClone(project.brand ?? {});
  const form = { tab: !!brand.enquireTab, label: brand.enquireLabel ?? '', on: !!project.lead_enabled, required: !!project.lead_required, emails: project.lead_email ?? '' };
  let dirty = false;

  page.innerHTML = html`
    <div class="page-head"><div><h1>Enquiries</h1><p class="sub">What buyers sent from the tour, and how the form works.</p></div>
      <div class="btn-row"><span class="muted small" id="e-note"></span><button class="btn primary" id="e-save" disabled>Saved</button></div></div>
    <div class="enq-grid">
      <section class="box enq-list-box">
        <div class="enq-list-head"><h2>Received</h2>
          <div class="enq-list-tools">
            ${marks ? html`<div class="seg small" id="e-filter"><button data-f="all">All</button><button data-f="new" id="e-new-btn">New</button></div>` : ''}
            <button class="btn small" id="e-csv" ${leads.length ? '' : 'disabled'}>${icon('download', 15)}Download CSV</button>
          </div></div>
        <div id="e-list"></div>
      </section>
      <div class="enq-side">
        <section class="box enq-form">
          <h2>The form</h2>
          <label class="toggle"><input type="checkbox" id="f-tab"><span>Enquire tab in the tour <em>A tab on the side that opens a short form, any time.</em></span></label>
          <label class="field" id="f-label-row"><span>Tab wording</span><input id="f-label" maxlength="24" placeholder="Enquire"></label>
          <label class="toggle"><input type="checkbox" id="f-on"><span>Form on the loading screen <em>Name, email and phone before they explore.</em></span></label>
          <label class="toggle sub-toggle" id="f-req-row"><input type="checkbox" id="f-req"><span>Ask before they can explore <em>Otherwise buyers can skip it.</em></span></label>
          <p class="muted small" style="margin:0">${project.state === 'published' ? 'The form is live on the tour.' : 'Enquiries are collected once the tour is published.'} See how it looks in <a href="#/p/${project.id}/brand/loading">Brand kit</a>.</p>
        </section>
        <section class="box enq-form">
          <h2>Send enquiries to</h2>
          <label class="field"><span>Email addresses</span><input id="f-emails" type="text" placeholder="sales@agency.com.au" autocomplete="off">
            <em class="muted small hint">Up to three, separated by commas. Each enquiry is emailed as it arrives, and replying goes straight to the buyer.</em></label>
        </section>
      </div>
    </div>`.html;

  const leadHtml = (l: LeadRow): Raw => {
    const isNew = marks && !l.read_at;
    return html`<article class="enq-item ${isNew ? 'new' : ''}" data-id="${l.id}">
      <div class="enq-top"><strong>${isNew ? html`<i class="enq-dot" aria-label="New"></i>` : ''}${l.name || 'No name given'}</strong><span class="muted small">${fmtWhen(l.created_at)}</span></div>
      <div class="enq-meta"><a href="mailto:${l.email}" data-open="${l.id}">${l.email}</a>${l.phone ? html` · <a href="tel:${l.phone.replace(/\s/g, '')}" data-open="${l.id}">${l.phone}</a>` : ''} · ${fromWhere(l)}${l.consent ? '' : ' · not to be contacted for marketing'}</div>
      ${l.message ? html`<p class="enq-msg">${l.message}</p>` : ''}
      ${marks ? html`<button class="link-btn small" data-toggle="${l.id}">${isNew ? 'Mark as read' : 'Mark as new'}</button>` : ''}
    </article>`;
  };
  const drawList = () => {
    const unread = leads.filter((l) => marks && !l.read_at).length;
    setUnread(root, unread);
    const nb = page.querySelector('#e-new-btn'); if (nb) nb.textContent = unread ? `New · ${unread}` : 'New';
    $$<HTMLButtonElement>('[data-f]', page).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.f === filter)));
    const list = filter === 'new' ? leads.filter((l) => !l.read_at) : leads;
    $('#e-list', page).innerHTML = list.length
      ? html`${list.map(leadHtml)}${unread > 1 ? html`<div class="enq-foot"><button class="link-btn small" id="e-all-read">Mark all as read</button></div>` : ''}`.html
      : html`<p class="muted small enq-empty">${filter === 'new' ? 'Nothing new. You’re up to date.' : 'No enquiries yet. They appear here once buyers fill in the form on the published tour.'}</p>`.html;
  };
  const setRead = async (ids: string[] | null, read: boolean) => {
    const before = leads.map((l) => l.read_at);
    leads = leads.map((l) => (!ids || ids.includes(l.id) ? { ...l, read_at: read ? l.read_at ?? new Date().toISOString() : null } : l));
    drawList();
    try { await markLeadsRead(project.id, ids, read); }
    catch (e) { leads = leads.map((l, i) => ({ ...l, read_at: before[i] })); drawList(); toast(`Couldn’t update: ${e instanceof Error ? e.message : 'unknown error'}`, 'error', 7000); }
  };
  $('#e-list', page).addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const tog = t.closest<HTMLElement>('[data-toggle]');
    if (tog) { const l = leads.find((x) => x.id === tog.dataset.toggle); if (l) void setRead([l.id], !!(marks && !l.read_at)); return; }
    if (t.closest('#e-all-read')) { void setRead(null, true); return; }
    // emailing or calling a buyer counts as reading it
    const open = t.closest<HTMLElement>('[data-open]');
    if (open) { const l = leads.find((x) => x.id === open.dataset.open); if (l && marks && !l.read_at) void setRead([l.id], true); }
  });
  $$<HTMLButtonElement>('[data-f]', page).forEach((b) => (b.onclick = () => { filter = b.dataset.f as typeof filter; drawList(); }));
  $<HTMLButtonElement>('#e-csv', page).onclick = () => downloadLeads(project.name, leads);

  // ------------------------------------------------------------ the form
  const drawForm = () => {
    $<HTMLInputElement>('#f-tab', page).checked = form.tab;
    $('#f-label-row', page).hidden = !form.tab;
    $<HTMLInputElement>('#f-on', page).checked = form.on;
    $<HTMLInputElement>('#f-req', page).checked = form.required;
    $('#f-req-row', page).hidden = !form.on;
  };
  $<HTMLInputElement>('#f-label', page).value = form.label;
  $<HTMLInputElement>('#f-emails', page).value = form.emails;
  const touch = () => { dirty = true; const s = $<HTMLButtonElement>('#e-save', page); s.disabled = false; s.textContent = 'Save'; $('#e-note', page).textContent = 'Unsaved changes'; drawForm(); };
  $<HTMLInputElement>('#f-tab', page).onchange = (e) => { form.tab = (e.target as HTMLInputElement).checked; touch(); };
  $<HTMLInputElement>('#f-label', page).oninput = (e) => { form.label = (e.target as HTMLInputElement).value.trim(); touch(); };
  $<HTMLInputElement>('#f-on', page).onchange = (e) => { form.on = (e.target as HTMLInputElement).checked; if (!form.on) form.required = false; touch(); };
  $<HTMLInputElement>('#f-req', page).onchange = (e) => { form.required = (e.target as HTMLInputElement).checked; touch(); };
  $<HTMLInputElement>('#f-emails', page).oninput = (e) => { form.emails = (e.target as HTMLInputElement).value.trim(); touch(); };

  // save: the project row, then the tour file (and the live tour)
  $<HTMLButtonElement>('#e-save', page).onclick = async () => {
    const s = $<HTMLButtonElement>('#e-save', page); s.disabled = true; s.textContent = 'Saving…';
    try {
      const emails = form.emails.split(/[,;\s]+/).filter(Boolean);
      const bad = emails.find((x) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
      if (bad) throw new Error(`“${bad}” doesn’t look like an email address`);
      if (emails.length > 3) toast('Only the first three addresses are kept.', 'info', 6000);
      if ((form.tab || form.on) && !emails.length) toast('Add an email to have enquiries sent to you. They’re still listed here.', 'info', 7000);
      // the rest of the brand may have changed in Brand kit meanwhile: only the tab's two fields come from here
      const fresh = (await getProject(project.id)) ?? project;
      const b: ProjectBrand = { ...(fresh.brand ?? {}), enquireTab: form.tab || undefined, enquireLabel: form.label || undefined };
      const lead_email = emails.slice(0, 3).join(', ') || null;
      await update('projects', `id=eq.${project.id}`, { brand: b, lead_enabled: form.on, lead_required: form.required, lead_email });
      Object.assign(project, { brand: b, lead_enabled: form.on, lead_required: form.required, lead_email });
      if (panos.length) {
        const man = await getDraftManifest(project.id);
        man.brand = manifestBrand(b, new Map(panos.map((p) => [p.id, p.base_key])));
        man.lead = { enabled: form.on, required: form.required };
        await uploadFile(project.id, 'manifest.json', new Blob([JSON.stringify(man)], { type: 'application/json' }));
        if (project.state === 'published') await syncPublished(project.id);
      }
      dirty = false; s.textContent = 'Saved';
      $('#e-note', page).textContent = project.state === 'published' ? 'Saved. The live tour is updated.' : 'Saved';
      toast('Enquiry settings saved');
    } catch (err) { s.disabled = false; s.textContent = 'Save'; toast(`Couldn’t save: ${err instanceof Error ? err.message : 'unknown error'}`, 'error', 8000); }
  };
  const guard = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
  addEventListener('beforeunload', guard);
  addEventListener('hashchange', () => removeEventListener('beforeunload', guard), { once: true });

  drawForm();
  drawList();
}
